import { useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { changePassword } from '../../../store/slices/authSlice';
import toast from 'react-hot-toast';
import ProfileSidebar from '../../components/ProfileSidebar/ProfileSidebar';
import PasswordGenerator from '../../../components/PasswordGenerator/PasswordGenerator';
import { useForm } from '../../hooks';
import { validatePassword, generateRandomPassword } from '../../utils';

// Calculate password strength
const calculatePasswordStrength = (password) => {
  if (!password) return { strength: 0, label: '', color: '' };
  
  let strength = 0;
  
  // Length check
  if (password.length >= 8) strength += 1;
  if (password.length >= 11) strength += 1;
  
  // Character variety checks
  if (/[a-z]/.test(password)) strength += 1;
  if (/[A-Z]/.test(password)) strength += 1;
  if (/[0-9]/.test(password)) strength += 1;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 1;
  
  // Determine strength level
  if (strength <= 2) {
    return { strength: 25, label: 'Weak', color: '#ef4444' }; // red
  } else if (strength <= 4) {
    return { strength: 50, label: 'Medium', color: '#f97316' }; // orange
  } else {
    return { strength: 100, label: 'Strong', color: '#22c55e' }; // green
  }
};

// Form validation function
const validatePasswordForm = (values) => {
  const errors = {};
  
  if (!values.current) {
    errors.current = 'Please enter your current password.';
  }
  
  if (!values.new) {
    errors.new = 'Please enter your new password.';
  } else {
    const passwordValidation = validatePassword(values.new);
    if (!passwordValidation.isValid) {
      errors.new = 'New Password must include at least 11 characters consisting of lower & upper case letters, numbers and special characters.';
    }
  }
  
  if (!values.confirm) {
    errors.confirm = 'To confirm, please enter your new password again.';
  } else if (values.new && values.confirm && values.new !== values.confirm) {
    errors.confirm = 'Passwords do not match';
  }
  
  return errors;
};

const ChangePasswordPage = () => {
  const dispatch = useAppDispatch();
  const { passwordChangeLoading } = useAppSelector((state) => state.auth);
  
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordGenerator, setShowPasswordGenerator] = useState(false);
  
  const {
    values: passwords,
    errors,
    touched,
    isSubmitting,
    setIsSubmitting,
    handleChange,
    handleBlur,
    validate,
    reset
  } = useForm(
    {
      current: '',
      new: '',
      confirm: ''
    },
    validatePasswordForm
  );

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const handleLockButtonClick = useCallback(() => {
    const randomPassword = generateRandomPassword();
    handleChange('new', randomPassword);
    handleChange('confirm', randomPassword);
  }, [handleChange]);

  const handleAdvancedSettingToggle = useCallback(() => {
    setShowPasswordGenerator(true);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Dispatch change password action with the correct field names
      await dispatch(changePassword({
        current_password: passwords.current,
        password: passwords.new
      })).unwrap();

      // Show success toast
      toast.success('Password changed successfully! Please log in again with your new password.');
      
      // Reset form
      reset();
      
    } catch (error) {
      // Show error toast
      toast.error(error || 'Failed to change password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, validate, reset, setIsSubmitting, passwords.current, passwords.new]);

  return (
    <div className="bg-jet-gray-100 pt-[40px] pb-8 px-3 sm:px-5">
      <div className="bg-white p-[28px] rounded-[10px] max-w-7xl mx-auto flex flex-col lg:flex-row gap-5">
        <ProfileSidebar />

        <div className="flex-1 bg-white rounded-lg shadow-jet overflow-hidden">
          <div className="bg-jet-yellow px-5 text-center py-3 border-b border-yellow-200 rounded-t-[calc(0.25rem-1px)] flex items-center justify-center">
            <h3 className="text-[18px] font-semibold text-[#000] m-0">Password</h3>
          </div>
          
          <div className="p-3 sm:p-5">
            <PasswordForm
              passwords={passwords}
              errors={errors}
              touched={touched}
              showPassword={showPassword}
              isSubmitting={isSubmitting || passwordChangeLoading}
              onPasswordChange={handleChange}
              onPasswordBlur={handleBlur}
              onTogglePasswordVisibility={togglePasswordVisibility}
              onLockButtonClick={handleLockButtonClick}
              onAdvancedSettingToggle={handleAdvancedSettingToggle}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>

      {/* Password Generator Modal */}
      <PasswordGenerator 
        isOpen={showPasswordGenerator} 
        onClose={() => setShowPasswordGenerator(false)}
        onPasswordGenerated={(generatedPassword) => {
          handleChange('new', generatedPassword);
          handleChange('confirm', generatedPassword);
        }}
      />
    </div>
  );
};

// Extracted form component for better maintainability
const PasswordForm = ({
  passwords,
  errors,
  touched,
  showPassword,
  isSubmitting,
  onPasswordChange,
  onPasswordBlur,
  onTogglePasswordVisibility,
  onLockButtonClick,
  onAdvancedSettingToggle,
  onSubmit
}) => (
  <div className="flex justify-center items-start p-8 pt-2">
    <form className="w-full max-w-[20rem] flex flex-col gap-4" onSubmit={onSubmit} noValidate>
      <PasswordField
        type="password"
        placeholder="Current Password"
        value={passwords.current}
        error={touched.current && errors.current}
        onChange={(value) => onPasswordChange('current', value)}
        onBlur={() => onPasswordBlur('current')}
        required
      />
      
      <PasswordFieldWithToggle
        placeholder="New Password"
        value={passwords.new}
        error={touched.new && errors.new}
        showPassword={showPassword}
        onChange={(value) => onPasswordChange('new', value)}
        onBlur={() => onPasswordBlur('new')}
        onToggleVisibility={onTogglePasswordVisibility}
        onLockButtonClick={onLockButtonClick}
        onAdvancedToggle={onAdvancedSettingToggle}
        required
      />
      
      <PasswordField
        type="password"
        placeholder="Confirm Password"
        value={passwords.confirm}
        error={touched.confirm && errors.confirm}
        onChange={(value) => onPasswordChange('confirm', value)}
        onBlur={() => onPasswordBlur('confirm')}
        required
      />
      
      <div className="flex justify-center mt-4 mb-4">
        <button 
          type="submit" 
          className="block w-full px-6 py-1.5 text-[0.875rem] leading-7 font-bold bg-jet-yellow text-jet-gray-800 border border-transparent rounded-[0.3125rem] h-[40px] cursor-pointer min-w-[2.375rem] transition-[color,background-color,border-color,box-shadow] duration-150 ease-in-out hover:bg-jet-yellow-hover hover:-translate-y-0.5 focus:outline-none focus:shadow-[0_0_0_2px_rgba(255,215,0,0.4)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-jet-yellow"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'SAVE'}
        </button>
      </div>
    </form>
  </div>
);

const PasswordField = ({ 
  type = "password", 
  placeholder, 
  value, 
  error, 
  onChange, 
  onBlur, 
  required = false 
}) => (
  <div className="flex flex-col">
    <label className="text-jet-gray-500 text-sm mb-1">{placeholder}</label>
    <input 
      type={type}
      className={`password-field inline-block w-full px-3 py-1.5 text-[0.925rem] leading-[1.6] font-medium text-[#111236] h-[42px] bg-white bg-clip-padding border rounded-[5px] shadow-none appearance-none transition-[border-color,box-shadow] duration-150 ease-in-out opacity-100 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)] placeholder:text-jet-gray-400 ${error ? 'border-jet-red-500 shadow-[0_0_0_1px_#ef4444]' : 'border-[#a1a1a1]'}`}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      required={required}
      aria-invalid={!!error}
      aria-describedby={error ? `${placeholder}-error` : undefined}
    />
    {error && (
      <div id={`${placeholder}-error`} className="text-[#ef4444] text-sm mt-2" role="alert">
        {error}
      </div>
    )}
  </div>
);

const PasswordFieldWithToggle = ({
  placeholder,
  value,
  error,
  showPassword,
  onChange,
  onBlur,
  onToggleVisibility,
  onLockButtonClick,
  onAdvancedToggle,
  required = false
}) => {
  const passwordStrength = calculatePasswordStrength(value);
  
  return (
    <div className="flex flex-col">
      <label className="text-jet-gray-500 text-sm mb-1">{placeholder}</label>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex items-center flex-1">
          <input 
            type={showPassword ? "text" : "password"}
            className={`password-field inline-block w-full px-3 py-1.5 pr-12 text-[0.925rem] leading-[1.6] font-medium text-[#111236] h-[42px] bg-white bg-clip-padding border rounded-[5px] shadow-none appearance-none transition-[border-color,box-shadow] duration-150 ease-in-out opacity-100 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)] placeholder:text-jet-gray-400 ${error ? 'border-jet-red-500 shadow-[0_0_0_1px_#ef4444]' : 'border-[#a1a1a1]'}`}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            required={required}
            aria-invalid={!!error}
            aria-describedby={error ? `${placeholder}-error` : undefined}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-jet-gray-500 z-10 p-1 hover:text-jet-gray-700"
            onClick={onToggleVisibility}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true"></i>
          </button>
        </div>
        <button
          type="button"
          className="cursor-pointer bg-jet-yellow rounded w-10 h-10 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0 hover:bg-jet-yellow-hover self-end sm:self-auto"
          onClick={onLockButtonClick}
          aria-label="Generate random password"
          title="Generate random password"
        >
          <i className="fas fa-lock text-black text-base" aria-hidden="true"></i>
        </button>
      </div>
      
      {/* Password Strength Indicator */}
      {value && (
        <div className="mt-2">
          <div className="flex gap-1 h-1">
            <div 
              className="flex-1 rounded transition-all duration-300"
              style={{ 
                backgroundColor: passwordStrength.strength >= 25 ? passwordStrength.color : '#e5e7eb' 
              }}
            />
            <div 
              className="flex-1 rounded transition-all duration-300"
              style={{ 
                backgroundColor: passwordStrength.strength >= 50 ? passwordStrength.color : '#e5e7eb' 
              }}
            />
            <div 
              className="flex-1 rounded transition-all duration-300"
              style={{ 
                backgroundColor: passwordStrength.strength >= 75 ? passwordStrength.color : '#e5e7eb' 
              }}
            />
            <div 
              className="flex-1 rounded transition-all duration-300"
              style={{ 
                backgroundColor: passwordStrength.strength >= 100 ? passwordStrength.color : '#e5e7eb' 
              }}
            />
          </div>
        </div>
      )}
      
      {error && (
        <div id={`${placeholder}-error`} className="text-[#ef4444] text-sm mt-2" role="alert">
          {error}
        </div>
      )}
      
      <div className="flex justify-end mt-1">
        <span 
          className="text-xs text-jet-gray-500 text-right cursor-pointer hover:text-jet-yellow transition-colors"
          onClick={onAdvancedToggle}
        >
          Advanced Setting
        </span>
      </div>
    </div>
  );
};

export default ChangePasswordPage;